/**
 * ImageThumb — renders a stored image (by image_id) as an <img>.
 *
 * The image bytes are org-scoped + auth-gated, so a raw `<img src="/v1/twin/images/…">`
 * can't send the auth header. Instead we fetch the bytes as an authenticated Blob,
 * make an object URL, and revoke it on unmount / id-change. Pure <img> (no onClick) so
 * it stays a11y-clean; callers wrap it in a <button> for lightbox opening.
 */

import { useEffect, useState } from "react";
import { cn } from "@/lib/ui/cn";
import { images } from "@/lib/api";

export function ImageThumb({
  imageId,
  alt,
  className,
}: {
  imageId: string;
  alt?: string;
  className?: string;
}) {
  // Keyed by imageId so a stale image never renders while the new one loads,
  // without resetting state synchronously in the effect body.
  const [loaded, setLoaded] = useState<{ id: string; url: string } | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    images
      .blob(imageId)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setLoaded({ id: imageId, url: objectUrl });
      })
      .catch(() => {
        if (active) setFailedId(imageId);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageId]);

  const url = loaded?.id === imageId ? loaded.url : null;
  const failed = failedId === imageId;

  if (failed) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-surface-muted text-xs text-on-surface-muted",
          className,
        )}
      >
        —
      </div>
    );
  }
  if (!url) {
    return <div className={cn("animate-pulse bg-surface-muted", className)} />;
  }
  return <img src={url} alt={alt ?? ""} className={className} loading="lazy" />;
}
